import { LoadingContent } from "@/components/LoadingContent";
import { useEffect, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Group,
  Stack,
  Text,
} from "@mantine/core";

import { CenteredCard } from "@/components/CenteredCard";
import { useAppSession } from "@/features/auth/session";
import { useAuthMethods } from "@/features/auth/hooks";
import { AccountChoice } from "@/components/AccountChoice";
import { MobileHandoff } from "@/components/MobileHandoff";
import { ApiError } from "@/lib/apiClient";
import {
  fetchInvitationPreview,
  invitationSearchSchema,
  respondToInvitation,
  type InvitationAction,
  type InvitationPreview,
} from "@/features/invitations/api";
import { describeInvitationError } from "@/features/invitations/errors";
import {
  expiryLabel,
  pendingHeading,
  roleDescription,
  roleLabel,
  settledHeading,
} from "@/features/invitations/copy";
import {
  clearPendingInvitation,
  readPendingInvitation,
  stashPendingInvitation,
} from "@/features/invitations/pendingInvitation";

/**
 * The page every trip invitation email links to:
 *
 *   ${APP_URL}/trips/invitations/respond?token=<raw>&action=accept
 *
 * The link is a GET; the API endpoint is a POST. Bridging those two is this
 * page's whole job, and the gap is deliberate — see the note on confirmation
 * below.
 *
 * `GET /v1/trips/invitations/preview` fills in who is inviting whom to what. It
 * reads the invitation without consuming it, which is the only reason this page
 * can name the trip at all: the respond endpoint is the one that acts, so
 * asking it would answer the question by ending the invitation.
 */

function buildSelfUrl(
  token: string,
  action: InvitationAction | undefined,
): string {
  const params = new URLSearchParams({ token });
  if (action) params.set("action", action);
  return `${window.location.origin}/trips/invitations/respond?${params.toString()}`;
}

function RespondPage() {
  const { token, action } = Route.useSearch();
  const navigate = useNavigate();
  const { registrationEnabled } = useAuthMethods();
  const {
    data: session,
    isPending: sessionPending,
    error: sessionError,
    refetch: retrySession,
  } = useAppSession();

  const previewQuery = useQuery({
    queryKey: ["invitation", "preview", token],
    queryFn: () => fetchInvitationPreview(token as string),
    enabled: Boolean(token),
    // The token is a bearer credential and the endpoint is rate limited to
    // 5/min/IP — the same budget as responding. Retrying a rejected token only
    // spends that budget on an answer that will not change.
    retry: false,
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: respondToInvitation,
    onSuccess: clearPendingInvitation,
  });

  // Guards the auto-resume below against React's double-invoked effects in
  // development, which would otherwise fire two POSTs for one return trip.
  const resumedRef = useRef(false);

  const isAuthenticated = Boolean(session?.user);

  /**
   * Resume an accept that was interrupted by the sign-in detour.
   *
   * Safe to run without a click, unlike the initial submit: it requires an
   * authenticated session, which an email scanner or link prefetcher can never
   * have. The stashed token must also match the one in the URL, so a stale stash
   * from an earlier invitation in the same tab cannot act on this one.
   */
  useEffect(() => {
    if (
      resumedRef.current ||
      !token ||
      !isAuthenticated ||
      mutation.isPending ||
      previewQuery.data?.status !== "PENDING"
    )
      return;

    const pending = readPendingInvitation();
    if (!pending || pending.token !== token) return;

    resumedRef.current = true;
    mutation.mutate({ token: pending.token, action: pending.action });
  }, [token, isAuthenticated, mutation, previewQuery.data]);

  if (sessionError)
    return (
      <CenteredCard
        title="Could not check your account"
        description={sessionError.message}
      >
        <Button onClick={() => void retrySession()}>Try again</Button>
      </CenteredCard>
    );

  // --- Malformed link -------------------------------------------------------

  if (!token) {
    return (
      <CenteredCard
        title="This invitation link isn't complete"
        description="The link is missing its invitation token. Some email clients shorten long links — try opening it directly from the original email, or ask the trip owner to resend the invitation."
      />
    );
  }

  const preview: InvitationPreview | undefined = previewQuery.data;
  const isOwnership = preview?.type === "OWNERSHIP_TRANSFER";

  // --- Done -----------------------------------------------------------------

  if (mutation.isSuccess) {
    const accepted = mutation.variables?.action === "accept";
    const tripName = preview?.tripName;

    let title = "Invitation declined";
    let description =
      "Thanks for letting us know — we've told the trip owner. Nothing else to do.";
    if (accepted) {
      title = "You're in";
      description = `You've joined ${tripName ?? "the trip"}. View it in this browser or open the mobile app.`;
      if (isOwnership) {
        title = "Ownership accepted";
        description = `Ownership of ${tripName ?? "this trip"} has been transferred to the invited account.`;
      }
    }
    return (
      <CenteredCard title={title} description={description}>
        {accepted ? (
          <MobileHandoff tripId={mutation.data.tripId} showBrowserLink />
        ) : null}
      </CenteredCard>
    );
  }

  // --- Failed ---------------------------------------------------------------

  if (mutation.isError) {
    const view = describeInvitationError(mutation.error);
    const reauthenticate =
      mutation.error instanceof ApiError &&
      mutation.error.status === 401 &&
      (!mutation.error.code || mutation.error.code === "ACCOUNT_UNAUTHORIZED");
    return (
      <CenteredCard title={view.title} description={view.detail}>
        {reauthenticate ? (
          <Button
            onClick={() => {
              stashPendingInvitation({ token, action: "accept" });
              window.location.assign(
                `/auth/sign-in?${new URLSearchParams({ next: buildSelfUrl(token, "accept") })}`,
              );
            }}
          >
            Sign in again
          </Button>
        ) : null}
        {view.retryable ? (
          <Button
            variant="default"
            onClick={() => {
              mutation.reset();
              void previewQuery.refetch();
            }}
          >
            Try again
          </Button>
        ) : null}
      </CenteredCard>
    );
  }

  // --- Waiting on the preview -----------------------------------------------

  // Keep actions visible but unavailable until the invitation state is known.
  if (previewQuery.isPending) {
    return (
      <CenteredCard
        title="Trip invitation"
        description="Looking up your invitation…"
      >
        <Stack><LoadingContent label="Looking up your invitation…" variant="rows" rows={2} /><Group grow>{action !== "decline" ? <Button disabled>Accept invitation</Button> : null}<Button variant="default" disabled>Decline invitation</Button></Group></Stack>
      </CenteredCard>
    );
  }

  // --- Preview failed -------------------------------------------------------

  // A 404 is definitive: the API does not recognise this token, so the POST
  // would fail the same way and there is nothing to offer. Anything else (a
  // network blip, a 500, a rate limit) says nothing about the invitation
  // itself, so the page falls through to the generic copy below and still lets
  // the recipient act. The preview is decoration; the token is what counts.
  if (
    previewQuery.error instanceof ApiError &&
    previewQuery.error.status === 404
  ) {
    const view = describeInvitationError(previewQuery.error);
    return <CenteredCard title={view.title} description={view.detail} />;
  }

  // --- Already settled ------------------------------------------------------

  // Reached without a POST: the preview reports accepted, declined, cancelled
  // and expired invitations, so someone re-opening an old email gets an
  // explanation rather than a button that returns an error.
  if (preview && preview.status !== "PENDING") {
    const settled = settledHeading(preview);
    return (
      <CenteredCard title={settled.title} description={settled.description}>
        {preview.status === "ACCEPTED" ? (
          <MobileHandoff showBrowserLink />
        ) : null}
      </CenteredCard>
    );
  }

  // --- Accepting requires an account ----------------------------------------

  // The API can only attach a trip member row to a real user, so an
  // unauthenticated accept is sent through auth first rather than being POSTed
  // and failing. Declining needs no account — the token alone identifies the
  // invitation — so it stays available either way.
  const showAccept = action !== "decline";
  const needsAuthToAccept = showAccept && !isAuthenticated && !sessionPending;

  const goToAuth = (target: "/auth/sign-in" | "/auth/sign-up") => {
    stashPendingInvitation({ token, action: "accept" });
    void navigate({
      to: target,
      search: { next: buildSelfUrl(token, "accept") },
    });
  };

  let acceptLabel = "Accept invitation";
  if (isOwnership) acceptLabel = "Accept ownership";
  if (needsAuthToAccept) acceptLabel = "Sign in to accept";

  const submit = (chosen: InvitationAction) => {
    if (chosen === "accept" && needsAuthToAccept) {
      goToAuth("/auth/sign-in");
      return;
    }
    mutation.mutate({ token, action: chosen });
  };

  /**
   * Nothing is submitted on load, even though `action` is right there in the
   * URL. Corporate mail scanners (Outlook SafeLinks and friends) fetch every
   * link in an incoming email, so an auto-submit would let a scanner silently
   * accept or decline invitations on the recipient's behalf. The click is the
   * only thing that proves a human is here.
   *
   * The preview GET is exempt by construction — it reads and changes nothing,
   * which is why it can run on load while the response cannot.
   */
  // An accept email link suggests the primary action, but the recipient may
  // still decline here without signing in or creating an account.

  // Falls back to the old generic copy when the preview did not come back. The
  // invitation is still actionable in that case; we just cannot name it.
  const heading = preview
    ? pendingHeading(preview)
    : {
        title: "Trip invitation",
        description: "You've been invited to join a trip on TripBears.",
      };

  const offeredRole = preview ? roleLabel(preview.role) : null;
  const roleDetail = preview ? roleDescription(preview.role) : null;
  const expiry = preview ? expiryLabel(preview.expiresAt) : null;

  return (
    <CenteredCard
      title={heading.title}
      description={heading.description}
      footer={
        needsAuthToAccept && registrationEnabled ? (
          <Text size="sm" c="dimmed">
            New to TripBears?{" "}
            <Text
              component="a"
              href="#"
              size="sm"
              c="var(--mantine-color-primary-text)"
              onClick={(event) => {
                event.preventDefault();
                goToAuth("/auth/sign-up");
              }}
            >
              Create an account
            </Text>
          </Text>
        ) : null
      }
    >
      <Stack gap="md">
        {session?.user ? (
          <AccountChoice
            email={session.user.email}
            returnTo={buildSelfUrl(token, action)}
          />
        ) : null}
        {offeredRole ? (
          <Group gap="xs" align="flex-start" wrap="nowrap">
            {/* Never shrink: the default flex basis truncates "CONTRIBUTOR" to
                an ellipsis next to the longer role description. */}
            <Badge variant="light" color="green" style={{ flexShrink: 0 }}>
              {offeredRole}
            </Badge>
            {roleDetail ? (
              <Text size="sm" c="dimmed">
                {roleDetail}
              </Text>
            ) : null}
          </Group>
        ) : null}

        {needsAuthToAccept ? (
          <Alert color="green" variant="light">
            {isOwnership
              ? "You'll need to sign in to the account this was sent to before taking ownership. Declining doesn't require an account."
              : "You'll need to sign in before joining the trip. Declining doesn't require an account."}
          </Alert>
        ) : null}

        <Stack gap="xs">
          {showAccept ? (
            <Button
              size="md"
              loading={
                mutation.isPending && mutation.variables?.action === "accept"
              }
              disabled={sessionPending || mutation.isPending}
              onClick={() => submit("accept")}
            >
              {acceptLabel}
            </Button>
          ) : null}

          <Button
            size={showAccept ? "xs" : "md"}
            variant={showAccept ? "subtle" : "default"}
            color={showAccept ? "gray" : undefined}
            style={showAccept ? { alignSelf: "center" } : undefined}
            loading={
              mutation.isPending && mutation.variables?.action === "decline"
            }
            disabled={mutation.isPending}
            onClick={() => submit("decline")}
          >
            Decline invitation
          </Button>
        </Stack>

        {expiry ? (
          <Text size="xs" c="dimmed" ta="center">
            {expiry}
          </Text>
        ) : null}
      </Stack>
    </CenteredCard>
  );
}

export const Route = createFileRoute("/trips/invitations/respond")({
  validateSearch: invitationSearchSchema,
  component: RespondPage,
});
